import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import LoadingOverlay from '../components/LoadingOverlay';
import './AppPage.css';
import './AuditPage.css';
import './AuditFilters.css';
import { translate } from '../utils/translations';
interface Log {
  id: string;
  createdAt: string;
  action: string;
  userType: string;
  description?: string;
  result: string;
  ipAddress?: string;
  employeeId?: string;
  employeeName?: string;
  receiptNumber?: string;
  period?: string;
  hasDocument?: boolean;
  metadata?: {
    comment?: string;
    signatureHash?: string;
    channel?: string;
    status?: string;
    eventKey?: string;
  };
}
const actionName = (x: Log) => translate(x.action);
const description = (x: Log) => {
  if (x.action === 'NOTIFICATION_DELIVERY_ATTEMPT')
    return `${x.metadata?.channel === 'EMAIL' ? 'Correo electrónico' : 'Notificación interna'}: ${translate(x.metadata?.status)}`;
  if (x.description === x.action) return translate(x.action);
  if (x.description?.startsWith(`${x.action}:`)) {
    const detail = x.description.slice(x.action.length + 1).trim();
    return /^[0-9a-f-]{36}$/i.test(detail)
      ? `${translate(x.action)} correctamente`
      : `${translate(x.action)}: ${detail}`;
  }
  return x.description || 'Operación registrada correctamente';
};
export default function AuditPage(): React.ReactElement {
  const nav = useNavigate();
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [employee, setEmployee] = useState('');
  const [type, setType] = useState('ALL');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const load = async () => {
    setLoading(true);
    try {
      setLogs((await apiClient.get('/audit')).data);
      setError('');
    } catch {
      setError('No se pudo cargar la evidencia de auditoría.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const employees = useMemo(
    () =>
      Array.from(
        new Map(
          logs
            .filter((x) => x.employeeId && x.employeeName)
            .map((x) => [x.employeeId!, x.employeeName!])
        ).entries()
      ).sort((a, b) => a[1].localeCompare(b[1])),
    [logs]
  );
  const shown = useMemo(
    () =>
      logs.filter((x) => {
        if (employee && x.employeeId !== employee) return false;
        if (type === 'CONFORMITY' && x.action !== 'RECEIPT_SIGNED_CONFORMITY') return false;
        if (type === 'NON_CONFORMITY' && x.action !== 'RECEIPT_SIGNED_NON_CONFORMITY') return false;
        if (type === 'SIGNED' && !x.action.startsWith('RECEIPT_SIGNED')) return false;
        const day = x.createdAt.slice(0, 10);
        return (!from || day >= from) && (!to || day <= to);
      }),
    [logs, employee, type, from, to]
  );
  const clear = () => {
    setEmployee('');
    setType('ALL');
    setFrom('');
    setTo('');
  };
  const view = async (x: Log) => {
    const tab = window.open('', '_blank');
    setBusy('Abriendo recibo…');
    try {
      const data = (await apiClient.get(`/audit/${x.id}/receipt`)).data;
      if (tab) tab.location.href = `data:${data.mimeType};base64,${data.documentData}`;
      else {
        const a = document.createElement('a');
        a.href = `data:${data.mimeType};base64,${data.documentData}`;
        a.download = data.fileName;
        a.click();
      }
    } catch {
      tab?.close();
      setError('No se pudo abrir el recibo asociado.');
    } finally {
      setBusy('');
    }
  };
  return (
    <div className="app-page">
      {busy && (
        <LoadingOverlay title={busy} detail="Recuperando el documento original de forma segura…" />
      )}
      <header className="app-bar">
        <button className="back-button" onClick={() => nav('/dashboard')}>
          ← Volver
        </button>
        <h1>Evidencia y auditoría</h1>
        <button className="secondary-button" onClick={() => void load()}>
          Actualizar
        </button>
      </header>
      <main className="app-main">
        <div className="page-heading">
          <div>
            <span className="eyebrow">TRAZABILIDAD</span>
            <h2>Historial verificable</h2>
            <p>Filtrá firmas y accedé al recibo original asociado a cada evidencia.</p>
          </div>
          <div className="summary-pill">
            <strong>{shown.length}</strong>
            <span>resultados</span>
          </div>
        </div>
        <section className="panel audit-filters">
          <div className="filter-field">
            <label>Empleado</label>
            <select value={employee} onChange={(e) => setEmployee(e.target.value)}>
              <option value="">Todos los empleados</option>
              {employees.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-field">
            <label>Tipo de firma</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="ALL">Todos los eventos</option>
              <option value="SIGNED">Todas las firmas</option>
              <option value="CONFORMITY">En conformidad</option>
              <option value="NON_CONFORMITY">En disconformidad</option>
            </select>
          </div>
          <div className="filter-field">
            <label>Desde</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="filter-field">
            <label>Hasta</label>
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <button className="secondary-button clear-filters" onClick={clear}>
            Limpiar
          </button>
        </section>
        {error && <div className="error-banner">{error}</div>}
        <section className="panel">
          {loading ? (
            <div className="page-message">Cargando evidencia…</div>
          ) : shown.length === 0 ? (
            <div className="empty-state">
              <strong>No hay resultados para estos filtros</strong>
              <span>Probá ampliar las fechas o cambiar el tipo de firma.</span>
            </div>
          ) : (
            <div className="audit-list">
              {shown.map((x) => (
                <article
                  className={`audit-event ${x.action === 'RECEIPT_SIGNED_NON_CONFORMITY' ? 'audit-observed' : ''}`}
                  key={x.id}
                >
                  <div className="audit-time">
                    <strong>{new Date(x.createdAt).toLocaleDateString('es-AR')}</strong>
                    <span>
                      {new Date(x.createdAt).toLocaleTimeString('es-AR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <div className="audit-detail">
                    <div>
                      <span className="audit-action">{actionName(x)}</span>
                      <span className={`audit-result ${x.result === 'SUCCESS' ? 'ok' : ''}`}>
                        {x.result === 'SUCCESS' ? 'Registrado' : translate(x.result)}
                      </span>
                    </div>
                    <strong>{description(x)}</strong>
                    <small>
                      {x.employeeName || translate(x.userType)}
                      {x.ipAddress ? ` · IP ${x.ipAddress}` : ''}
                      {x.period ? ` · Período ${x.period}` : ''}
                      {x.receiptNumber ? ` · Recibo N° ${x.receiptNumber}` : ''}
                    </small>
                    {x.metadata?.comment && <blockquote>“{x.metadata.comment}”</blockquote>}
                    {x.metadata?.signatureHash && (
                      <code title={x.metadata.signatureHash}>
                        Evidencia: {x.metadata.signatureHash.slice(0, 16)}…
                      </code>
                    )}
                  </div>
                  <div className="audit-actions">
                    {x.hasDocument && (
                      <button className="secondary-button" onClick={() => void view(x)}>
                        Ver recibo
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
